import React, { useState, useEffect } from 'react';
import './RegisterContact.css';
import { getHubSpotContext } from '../utils/hubspotContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function RegisterContact({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    email: '',
    firstname: '',
    lastname: '',
    company: '',
    jobtitle: '',
    phone: '',
    hub_id: '',
    hubspot_user_id: '',
    acta_plan: '',
    privacy_policy_form: false,
    term_of_service: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Auto-populate hub_id and hubspot_user_id from HubSpot context (URL params or localStorage)
  useEffect(() => {
    const context = getHubSpotContext();
    if (context) {
      setFormData(prev => ({
        ...prev,
        ...(context.portalId && { hub_id: context.portalId }),
        ...(context.userId && { hubspot_user_id: context.userId })
      }));
    }
  }, []);

  // Fetch available plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/hubspot/plans`);
        const data = await response.json();
        if (data.success && data.data.length > 0) {
          setPlans(data.data);
          // Set default to first plan (usually Free/Trial)
          setFormData(prev => ({ ...prev, acta_plan: data.data[0].code }));
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/hubspot/create-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to create contact');
      }

      setSuccess(true);
      if (onSuccess) {
        onSuccess(data.data);
      }
    } catch (err) {
      console.error('Error creating contact:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="register-contact-container">
        <div className="register-contact-card success">
          <div className="logo-container">
            <img src="/acta-logo.jpg" alt="Acta Extension" className="acta-logo" />
          </div>
          <div className="success-icon">✓</div>
          <h2>申込み完了</h2>
          <p>初回利用申込みが完了しました。</p>
          {onCancel && (
            <button className="btn btn-primary" onClick={onCancel}>
              閉じる
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="register-contact-container">
      <div className="register-contact-card">
        <div className="logo-container">
          <img src="/acta-logo.jpg" alt="Acta Extension" className="acta-logo" />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="company">会社名 *</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              placeholder="会社名を入力してください"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lastname">姓 *</label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                required
                placeholder="姓を入力してください"
              />
            </div>
            <div className="form-group">
              <label htmlFor="firstname">名 *</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                required
                placeholder="名を入力してください"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="jobtitle">役職</label>
            <input
              type="text"
              id="jobtitle"
              name="jobtitle"
              value={formData.jobtitle}
              onChange={handleChange}
              placeholder="役職を入力してください"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">電話番号 *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="電話番号を入力してください"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="メールアドレスを入力してください"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hub_id">HubSpot ポータルID *</label>
            <input
              type="text"
              id="hub_id"
              name="hub_id"
              value={formData.hub_id}
              onChange={handleChange}
              required
              pattern="^\d{6,10}$"
              title="HubSpot ポータルIDは6〜10桁の数字です"
              placeholder="例: 12345678"
            />
            <small className="form-hint">HubSpotの設定画面で確認できます（アカウントとセットアップ → アカウントの既定値 → ハブID）</small>
          </div>

          <div className="form-group">
            <label htmlFor="acta_plan">プラン *</label>
            <select
              id="acta_plan"
              name="acta_plan"
              value={formData.acta_plan}
              onChange={handleChange}
              required
              disabled={plansLoading}
            >
              {plansLoading ? (
                <option value="">読み込み中...</option>
              ) : (
                plans.map(plan => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name} {plan.price > 0 ? `(¥${plan.price.toLocaleString()}/月)` : '(無料)'}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="privacy_policy_form"
                checked={formData.privacy_policy_form}
                onChange={handleChange}
                required
              />
              <span><a href="/privacy-policy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意する</span>
            </label>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="term_of_service"
                checked={formData.term_of_service}
                onChange={handleChange}
                required
              />
              <span><a href="/terms-of-service" target="_blank" rel="noopener noreferrer">利用規約</a>に同意する</span>
            </label>
          </div>

          <div className="form-actions">
            {onCancel && (
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                キャンセル
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '送信中...' : '申込む'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterContact;
